import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Phone, Mail, MapPin, Package } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import CheckoutModal from "@/components/CheckoutModal";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PRODUCTS, REGIONS, BUSINESS } from "@/data/site";
import { WhatsAppIcon } from "@/components/Icons";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const p = PRODUCTS.find((x) => x.slug === slug);

  usePageMeta(
    p ? t.meta.product.title : t.notFound.title,
    t.meta.product.desc
  );

  if (!p) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-20">
        <div className="text-center">
          <p className="font-serif text-4xl text-cream">{t.notFound.title}</p>
          <Link
            to="/our-tea"
            data-testid="product-not-found-back-link"
            className="mt-8 inline-block border border-gold/50 px-7 py-3.5 text-sm uppercase tracking-[0.15em] text-gold hover:bg-gold/10"
          >
            {t.productPage.back}
          </Link>
        </div>
      </main>
    );
  }

  const d = t.productPage;

  return (
    <main className="overflow-hidden pb-24 pt-32 md:pt-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Link
            to="/our-tea"
            data-testid="product-back-to-all-teas-link"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-cream/60 transition-colors hover:text-gold"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {d.back}
          </Link>
        </Reveal>

        <div className="mt-10 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="relative">
            <div
              className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.15),transparent_65%)] blur-2xl"
              aria-hidden="true"
            />
            <div className="relative aspect-[4/5] overflow-hidden border hairline">
              <img
                src={p.image}
                alt={p.name}
                className="h-full w-full object-cover"
                data-testid="product-detail-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-obsidian/45 via-transparent to-transparent" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="aspect-[4/3] overflow-hidden border hairline">
                <img src={p.imageAlt} alt={`${p.name} loose leaf`} loading="lazy" className="h-full w-full object-cover" data-testid="product-detail-image-alt" />
              </div>
              <div className="aspect-[4/3] overflow-hidden border hairline">
                <img src={REGIONS[2].image} alt="Darjeeling tea hills" loading="lazy" className="h-full w-full object-cover" data-testid="product-detail-image-origin" />
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal delay={0.1}>
              <Eyebrow>{p.category[lang]}</Eyebrow>
              <h1 className="mt-5 font-serif text-4xl leading-tight text-cream sm:text-5xl lg:text-6xl">
                {p.name}
              </h1>

              <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3">
                <span className="font-serif text-5xl text-gold" data-testid="product-detail-price">
                  {p.currency}
                  {p.price}
                </span>
                <span className="border hairline px-4 py-2 text-[12px] uppercase tracking-widest text-cream/75" data-testid="product-detail-size">
                  {d.size}: {p.size}
                </span>
                <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest text-gold-light" data-testid="product-detail-availability">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" />
                  {d.availabilityValue}
                </span>
              </div>

              <div className="mt-10">
                <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">
                  {d.overview}
                </h2>
                {p.description[lang].split("\n\n").map((para, i) => (
                  <p key={i} className="mt-4 text-base leading-relaxed text-cream/70">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-9">
                <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">
                  {d.highlights}
                </h2>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {p.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-cream/75" data-testid={`product-highlight-${i + 1}`}>
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                      {h[lang]}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-9">
                <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">
                  {d.origin}
                </h2>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {REGIONS.map((r) => (
                    <span
                      key={r.id}
                      data-testid={`product-origin-chip-${r.id}`}
                      className="inline-flex items-center gap-2 border hairline bg-velvet/50 px-4 py-2 text-[12px] uppercase tracking-widest text-cream/75"
                    >
                      <MapPin className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                      {r.name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-11 flex flex-wrap gap-4">
                <button
                  onClick={() => setCheckoutOpen(true)}
                  data-testid="product-buy-now-button"
                  className="bg-gold px-9 py-4 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_36px_rgba(212,175,55,0.35)]"
                >
                  {d.buy}
                </button>
                <a
                  href={BUSINESS.tel}
                  data-testid="product-call-now-link"
                  className="inline-flex items-center gap-2.5 border border-gold/50 px-7 py-4 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.5} />
                  {d.call}
                </a>
                <a
                  href={BUSINESS.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-whatsapp-us-link"
                  className="inline-flex items-center gap-2.5 border border-gold/50 px-7 py-4 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  {d.whatsapp}
                </a>
                <a
                  href={BUSINESS.mailto}
                  data-testid="product-email-us-link"
                  className="inline-flex items-center gap-2.5 border border-gold/50 px-7 py-4 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
                >
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                  {d.email}
                </a>
              </div>

              <p className="mt-8 flex items-start gap-3 border-l-2 border-gold/50 bg-gold/5 px-5 py-4 text-[13px] leading-relaxed text-cream/60" data-testid="product-order-notice-inline">
                <Package className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                {d.inlineNote}
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} product={p} />
    </main>
  );
}
